import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import Swal from "sweetalert2";
import {
  FaEdit,
  FaTrashAlt,
  FaTimes,
  FaUpload,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaPlus,
  FaFilePdf,
  FaFileContract,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import "./AddAnnualReport.css";
import axiosInstance from "../../api/axiosInstance";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://localhost:5000";

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "-";

// --- ADD/EDIT MODAL ---
const ReportFormModal = ({ isOpen, onClose, data, mode, onSave }) => {
  const [formData, setFormData] = useState({ title: "", isActive: true });
  const [pdfFile, setPdfFile] = useState(null);
  const pdfInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (data) {
        setFormData({
          title: data.title || "",
          isActive: data.isActive,
        });
        setPdfFile(null);
      } else {
        setFormData({ title: "", isActive: true });
        setPdfFile(null);
      }
    }
  }, [data, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) setPdfFile(file);
  };

  const handleSubmit = () => {
    if (!formData.title)
      return Swal.fire("Error", "Please enter a title", "error");

    const submissionData = new FormData();
    submissionData.append("title", formData.title);
    submissionData.append("isActive", formData.isActive.toString());

    if (mode === "add" && !pdfFile) {
      return Swal.fire(
        "Error",
        "Please upload a PDF file for the report",
        "error",
      );
    }

    if (pdfFile) submissionData.append("pdf", pdfFile);

    onSave(submissionData);
  };

  if (!isOpen) return null;

  return (
    <div className="admin-add-report-page-modal-overlay">
      <div
        className="admin-add-report-page-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-add-report-page-modal-header">
          <h2>{data ? "Edit Annual Report" : "Add Annual Report"}</h2>
          <button
            className="admin-add-report-page-modal-close-btn"
            onClick={onClose}
          >
            <FaTimes />
          </button>
        </div>
        <div className="admin-add-report-page-modal-body">
          <div className="admin-add-report-page-form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="admin-add-report-page-input"
              placeholder="e.g. Annual Report 2024-25"
            />
          </div>

          <div className="admin-add-report-page-form-group">
            <label>Upload PDF Document</label>
            <div className="admin-add-report-page-pdf-upload-box">
              <input
                type="file"
                ref={pdfInputRef}
                onChange={handlePdfChange}
                accept="application/pdf"
                className="admin-add-report-page-input"
              />
              {data?.pdf && !pdfFile && (
                <small className="admin-add-report-page-hint">
                  Current PDF exists. Uploading a new file replaces it.
                </small>
              )}
            </div>
          </div>
        </div>
        <div className="admin-add-report-page-modal-footer">
          <button
            className="admin-add-report-page-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="admin-add-report-page-btn-save"
            onClick={handleSubmit}
          >
            Save Report
          </button>
        </div>
      </div>
    </div>
  );
};

// --- MAIN PANEL COMPONENT ---
const AddAnnualReport = () => {
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchReports = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/annual-reports");
      if (Array.isArray(response.data)) setReports(response.data);
      else setReports([]);
    } catch (error) {
      console.error("Error fetching reports:", error);
      if (error.response?.status !== 401) {
        Swal.fire("Error", "Failed to load report list", "error");
      }
      setReports([]);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const toggleRow = (id) => setExpandedRow((prev) => (prev === id ? null : id));

  const filteredData = useMemo(() => {
    return reports.filter((item) => {
      const term = searchTerm.toLowerCase();
      return item.title ? item.title.toLowerCase().includes(term) : false;
    });
  }, [reports, searchTerm]);

  const totalResults = filteredData.length;
  const totalPages = Math.ceil(totalResults / itemsPerPage);
  const startResult =
    totalResults === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endResult = Math.min(currentPage * itemsPerPage, totalResults);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const handleSaveItem = async (formData) => {
    try {
      if (modalMode === "edit") {
        await axiosInstance.put(
          `/api/annual-reports/${selectedItem._id}`,
          formData,
        );
      } else {
        await axiosInstance.post("/api/annual-reports", formData);
      }

      await fetchReports();
      Swal.fire(
        modalMode === "edit" ? "Updated!" : "Added!",
        "Operation completed.",
        "success",
      );
      setIsModalOpen(false);
    } catch (error) {
      console.error("Save Error:", error);
      if (error.response?.status !== 401) {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Server error occurred",
          "error",
        );
      }
    }
  };

  const handleDeleteItem = (id) => {
    Swal.fire({
      title: "Delete Report?",
      text: "Cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e74c3c",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await axiosInstance.delete(`/api/annual-reports/${id}`);
          await fetchReports();
          Swal.fire("Deleted", "", "success");
        } catch (error) {
          if (error.response?.status !== 401) {
            Swal.fire("Error", "Could not delete report.", "error");
          }
        }
      }
    });
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      await axiosInstance.put(`/api/annual-reports/${id}`, {
        isActive: !currentStatus,
      });
      await fetchReports();
    } catch (error) {
      if (error.response?.status !== 401) {
        Swal.fire("Error", "Could not update status.", "error");
      }
    }
  };

  const openModal = (mode, item = null) => {
    setModalMode(mode);
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>Annual Reports | Rajput Shiksha Kosh</title>

        <meta
          name="description"
          content="Annual Report , yearly reports "
        />
      </Helmet>
      <div className="admin-add-report-page-wrapper">
        <div className="admin-add-report-page-container">
          <ReportFormModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            data={selectedItem}
            mode={modalMode}
            onSave={handleSaveItem}
          />

          <h2 className="admin-add-report-page-title">
            <FaFileContract style={{ marginRight: "8px" }} /> Annual Report
            Management
          </h2>

          <div className="admin-add-report-page-controls">
            <div className="admin-add-report-page-search-wrapper">
              <FaSearch className="admin-add-report-page-search-icon" />
              <input
                type="text"
                placeholder="Search by report title..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="admin-add-report-page-search-input"
              />
            </div>
            <div className="admin-add-report-page-action-group">
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="admin-add-report-page-limit-select"
              >
                <option value="10">10 / page</option>
                <option value="20">20 / page</option>
                <option value="30">30 / page</option>
              </select>
              <button
                className="admin-add-report-page-add-btn"
                onClick={() => openModal("add")}
              >
                <FaPlus /> Add Annual Report
              </button>
            </div>
          </div>

          <div className="admin-add-report-page-table-card">
            <div className="admin-add-report-page-table-wrapper">
              <table className="admin-add-report-page-table">
                <thead>
                  <tr>
                    <th className="admin-add-report-page-col-sno">S.No</th>
                    <th className="admin-add-report-page-col-title">
                      Report Title
                    </th>
                    <th className="admin-add-report-page-col-pdf">Document</th>
                    <th className="admin-add-report-page-col-created">
                      Created At
                    </th>
                    <th className="admin-add-report-page-col-active">Status</th>
                    <th className="admin-add-report-page-col-actions">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="admin-add-report-page-no-data">
                        No annual reports found.
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((item, index) => (
                      <React.Fragment key={item._id}>
                        <tr className="admin-add-report-page-row">
                          <td className="admin-add-report-page-col-sno">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td className="admin-add-report-page-col-title">
                            <div className="admin-add-report-page-name-wrapper">
                              <span className="admin-add-report-text-bold">
                                {item.title}
                              </span>
                              <button
                                className="admin-add-report-page-mobile-toggle-btn"
                                onClick={() => toggleRow(item._id)}
                              >
                                {expandedRow === item._id ? (
                                  <FaChevronUp />
                                ) : (
                                  <FaChevronDown />
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="admin-add-report-page-col-pdf">
                            <a
                              href={`${API_BASE_URL}${item.pdf}`}
                              target="_blank"
                              rel="noreferrer"
                              className="admin-add-report-page-link-badge pdf"
                            >
                              <FaFilePdf /> Open PDF
                            </a>
                          </td>
                          <td className="admin-add-report-page-col-created">
                            {formatDate(item.createdAt)}
                          </td>
                          <td className="admin-add-report-page-col-active">
                            <label className="admin-add-report-page-switch">
                              <input
                                type="checkbox"
                                checked={item.isActive !== false}
                                onChange={() =>
                                  toggleActive(
                                    item._id,
                                    item.isActive !== false,
                                  )
                                }
                              />
                              <span className="admin-add-report-page-slider-toggle" />
                            </label>
                            <div
                              className={`admin-add-report-page-status-text ${item.isActive !== false ? "active" : "disabled"}`}
                            >
                              {item.isActive !== false ? "Active" : "Disabled"}
                            </div>
                          </td>
                          <td className="admin-add-report-page-col-actions">
                            <div className="admin-add-report-page-action-icons">
                              <button
                                className="admin-add-report-page-icon-btn edit"
                                onClick={() => openModal("edit", item)}
                              >
                                <FaEdit />
                              </button>
                              <button
                                className="admin-add-report-page-icon-btn delete"
                                onClick={() => handleDeleteItem(item._id)}
                              >
                                <FaTrashAlt />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedRow === item._id && (
                          <tr className="admin-add-report-page-details-row">
                            <td colSpan="6">
                              <div className="admin-add-report-page-details-content">
                                <div className="admin-add-report-page-detail-card">
                                  <div className="admin-add-report-page-detail-label">
                                    Title
                                  </div>
                                  <div className="admin-add-report-page-detail-text">
                                    {item.title}
                                  </div>
                                </div>
                                <div className="admin-add-report-page-detail-card">
                                  <div className="admin-add-report-page-detail-label">
                                    PDF Document
                                  </div>
                                  <a
                                    href={`${API_BASE_URL}${item.pdf}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="admin-add-report-page-expanded-pdf-link"
                                  >
                                    <FaFilePdf /> View Report PDF
                                  </a>
                                </div>
                                <div className="admin-add-report-page-detail-card">
                                  <div className="admin-add-report-page-detail-label">
                                    Created At
                                  </div>
                                  <div className="admin-add-report-page-detail-text">
                                    {formatDate(item.createdAt)}
                                  </div>
                                </div>
                                <div className="admin-add-report-page-detail-card">
                                  <div className="admin-add-report-page-detail-label">
                                    Status
                                  </div>
                                  <label className="admin-add-report-page-switch">
                                    <input
                                      type="checkbox"
                                      checked={item.isActive !== false}
                                      onChange={() =>
                                        toggleActive(
                                          item._id,
                                          item.isActive !== false,
                                        )
                                      }
                                    />
                                    <span className="admin-add-report-page-slider-toggle" />
                                  </label>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {paginatedData.length > 0 && (
            <div className="admin-add-report-page-pagination-container">
              <div className="admin-add-report-page-pagination-info">
                Showing {startResult} to {endResult} of {totalResults} results
              </div>
              <div className="admin-add-report-page-pagination-controls">
                <button
                  className="admin-add-report-page-page-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  Previous
                </button>
                <span className="admin-add-report-page-page-count">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <button
                  className="admin-add-report-page-page-btn"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AddAnnualReport;
